import clsx from 'clsx'
import { forwardRef, useId } from 'react'
import TextareaAutosize, {
  TextareaAutosizeProps
} from 'react-textarea-autosize'

import { cn } from '@/app/utils'

import { useMinMaxLengthMessage } from '../../input/hooks/useMinMaxLengthMessage'
import MinMaxLengthMessage from '../../input/ui/MinMaxLengthMessage'

export type Props = TextareaAutosizeProps & {
  invalid?: boolean
  inputClassName?: string
  maxLengthMessageFrom?: number
  dataTestId?: string
}

const AppTextareaAutoSize = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      className,
      invalid,
      inputClassName,
      placeholder,
      minLength,
      maxLength,
      maxLengthMessageFrom,
      onFocus,
      onBlur,
      dataTestId,
      ...props
    },
    ref
  ) => {
    const _id = useId()

    const {
      valueLength,
      isMinLengthInvalid,
      showMinLengthMessageValid,
      showMaxLengthMessageValid,
      setInputFocused
    } = useMinMaxLengthMessage({
      value: props.value,
      minLength,
      maxLengthMessageFrom
    })

    return (
      <>
        <div
          className={cn(
            'relative flex min-h-[120px] cursor-text items-end rounded-12 border-2 border-transparent bg-white font-pro',
            className,
            { 'border-red-80': invalid || isMinLengthInvalid }
          )}
        >
          <TextareaAutosize
            data-testid={dataTestId}
            id={_id}
            ref={ref}
            minLength={minLength}
            maxLength={maxLength}
            className={cn(
              'peer flex-grow',
              'min-h-[120px] resize-none px-12 py-16 pb-5 text-deepBlack outline-none transition text-16 focus:pt-25 md:pb-2 [&:not(:empty)]:pt-25',
              'placeholder:opacity-0',
              inputClassName,
              { 'text-red-80': invalid }
            )}
            placeholder={placeholder}
            onFocus={event => {
              setInputFocused(true)
              onFocus?.(event)
            }}
            onBlur={event => {
              setInputFocused(false)
              onBlur?.(event)
            }}
            {...props}
          />
          <label
            htmlFor={_id}
            className={clsx(
              'pointer-events-none absolute inset-x-0 top-0 z-10 bg-white px-12 pb-2 pt-5 text-black/50 transition-all text-12 md:pt-1',
              'peer-placeholder-shown:top-16 peer-placeholder-shown:bg-transparent peer-placeholder-shown:pb-0 peer-placeholder-shown:pt-0 peer-placeholder-shown:text-16',
              'peer-focus:top-0 peer-focus:bg-white peer-focus:pb-2 peer-focus:pt-5 peer-focus:text-black/30 peer-focus:text-12 md:peer-focus:pt-1',
              'peer-[:not(:placeholder-shown)]:text-black/30 peer-[:not(:placeholder-shown)]:text-12'
            )}
          >
            {placeholder}
          </label>
        </div>
        {Boolean(minLength) && (
          <MinMaxLengthMessage
            valueLength={valueLength}
            minLength={minLength}
            isLengthInvalid={isMinLengthInvalid}
            showMessageValid={showMinLengthMessageValid}
          />
        )}
        {Boolean(maxLength) && (
          <MinMaxLengthMessage
            valueLength={valueLength}
            maxLength={maxLength}
            showMessageValid={showMaxLengthMessageValid}
          />
        )}
      </>
    )
  }
)

AppTextareaAutoSize.displayName = 'AppTextareaAutoSize'

export default AppTextareaAutoSize
